import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, LayoutGroup, Variants } from 'framer-motion';
import { gameRedisService } from '../services/gameRedis';
import { configService } from '../services/usersApi';
import { Warp } from '../types/player';
import { usernameCacheService } from '../services/usernameCache';
import { PlayerProfile } from '../components/PlayerProfile';
import { ChevronDown, MapPin, Search, AlertCircle, X } from 'lucide-react';
import { Highlight } from '../components/Highlight';
import { parseNumbers, fuzzyMatchPartialCoordinates, parseCoordinates } from '../utils/coords';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { RedisKeyInfo } from '../components/RedisKeyInfo';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { DataFreshness } from '../components/DataFreshness';
import { useSwrLoader } from '../hooks/useSwrLoader';
import { read } from '../services/swrCache';

const modalContentVariants: Variants = {
  visible: {
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.07,
    },
  },
  hidden: {},
};

const warpCardVariants: Variants = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 20,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
  },
};

interface EnhancedWarp extends Warp {
  cluster: string;
}

interface WarpSearchResult extends EnhancedWarp {
  accuracy?: number;
}

interface WarpsByCluster {
  [clusterName: string]: EnhancedWarp[];
}

const ClusterCard = ({ clusterName, warps, thumbnailUrl, onOpen }: { clusterName: string, warps: EnhancedWarp[], thumbnailUrl?: string, onOpen: () => void }) => {
  const isUnknown = clusterName === 'Unknown';
  const displayName = isUnknown ? 'Offline / Uncategorized' : clusterName;
  const tooltip = isUnknown
    ? 'Warps for servers that are offline or not assigned to a cluster'
    : clusterName;

  return (
    <motion.div
      layoutId={`card-container-${clusterName}`}
      className="glass-card glass-card-interactive overflow-hidden"
      onClick={onOpen}
      title={tooltip}
    >
      <motion.div
        layoutId={`card-header-${clusterName}`}
        className="w-full p-4 flex justify-between items-center gap-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={clusterName}
              className="w-12 h-12 rounded-md object-cover"
            />
          )}
          <h2 className="text-lg font-semibold tracking-tight text-white">{displayName}</h2>
        </div>
        <div className="flex items-center gap-3 min-w-0">
          <span className="badge badge-accent">
            {warps.length} {warps.length === 1 ? 'Warp' : 'Warps'}
          </span>
          <ChevronDown className="w-4 h-4 text-glass-400" />
        </div>
      </motion.div>
    </motion.div>
  );
};

const WarpSearchResultCard = ({
  warp,
  query,
  thumbnailUrl,
  accuracy,
}: {
  warp: EnhancedWarp;
  query: string;
  thumbnailUrl?: string;
  accuracy?: number;
}) => {
  const isCoordSearch = accuracy !== undefined;

  return (
    <div className="glass-card p-4 space-y-3 flex flex-col h-full">
      <div className="flex items-center gap-3 min-w-0">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={warp.cluster}
            className="w-12 h-12 rounded-md object-cover"
          />
        )}
        <div>
          <h3 className="text-sm font-semibold text-white truncate">
            <Highlight text={warp.name} highlight={query} />
          </h3>
          <p className="text-xs text-glass-400">
            Cluster: <Highlight text={warp.cluster} highlight={query} />
          </p>
        </div>
      </div>
      <div className="space-y-2 text-sm border-t border-glass-200/10 pt-3 flex-grow">
        <div className="flex justify-between items-center">
          <span className="text-glass-400">Owner:</span>
          {/* Don't highlight the PlayerProfile component */}
          <PlayerProfile uuid={warp.owner} />
        </div>
        <div className="flex justify-between">
          <span className="text-glass-400">Server:</span>
          <span className="font-mono text-xs text-glass-300">
            <Highlight text={warp.fqdn} highlight={query} />
          </span>
        </div>
        {!isCoordSearch && (
          <div className="flex justify-between">
            <span className="text-glass-400">Position:</span>
            <span className="font-mono text-xs text-glass-300">
              <Highlight text={warp.pos} highlight={query} />
            </span>
          </div>
        )}
      </div>
      {isCoordSearch && (
        <div className="subcard p-2 mt-2 space-y-1">
          <div className="flex justify-between">
            <span className="text-glass-400">Position:</span>
            <span className="font-mono text-xs text-glass-300">
              <Highlight text={warp.pos} highlight={query} />
            </span>
          </div>
          <div className="text-right text-accent-300 font-semibold text-xs tabular-nums">
            Match Accuracy: {accuracy.toFixed(0)}%
          </div>
        </div>
      )}
    </div>
  );
};

export function WarpView() {
  useDocumentTitle('Warps');
  const [warpsByCluster, setWarpsByCluster] = useState<WarpsByCluster>({});
  const [warpsDataKey, setWarpsDataKey] = useState<string | null>(null);
  const [clusterThumbnails, setClusterThumbnails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCluster, setActiveCluster] = useState<string | null>(null);
  const { status: freshness, load } = useSwrLoader();

  // Cache, then recache on load: the last copy of the warp document paints at once (if there is
  // one), Redis is re-read every time, and the fresh document replaces it when it arrives.
  const fetchData = useCallback(async (force = false) => {
    let superseded = false;
    try {
      setLoading(true);
      setError(null);

      // The cluster mapping and configs load alongside the cache read, not before it.
      const configsPromise = Promise.all([configService.getClusterFqdnMapping(), configService.getConfigs()]);
      configsPromise.then(([, serverConfigs]) => {
        if (serverConfigs) {
          const thumbnails = serverConfigs.reduce((acc, config) => {
            if (config.cluster_name && config.thumbnail_url) {
              acc[config.cluster_name] = config.thumbnail_url;
            }
            return acc;
          }, {} as Record<string, string>);
          setClusterThumbnails(thumbnails);
        }
      }).catch(() => undefined);

      const outcome = await load(
        [
          read('redis:warps', () => gameRedisService.getWarpData(), async (warpDataResult) => {
            const [clusterMapping] = await configsPromise;
            const warpData = warpDataResult.data;
            setWarpsDataKey(warpDataResult.key);
            if (warpData && warpData.warps) {
              const fqdnToClusterMapping: Record<string, string> = {};
              for (const cluster in clusterMapping) {
                for (const fqdn of clusterMapping[cluster]) {
                  fqdnToClusterMapping[fqdn] = cluster;
                }
              }

              const enhancedWarps = warpData.warps.map(warp => ({
                ...warp,
                cluster: fqdnToClusterMapping[warp.fqdn] || 'Unknown',
              }));

              const uuids = Array.from(new Set(enhancedWarps.map(warp => warp.owner)));
              await usernameCacheService.prefetchUsernames(uuids);

              const groupedWarps = enhancedWarps.reduce((acc, warp) => {
                const { cluster } = warp;
                if (!acc[cluster]) {
                  acc[cluster] = [];
                }
                acc[cluster].push(warp);
                return acc;
              }, {} as WarpsByCluster);

              // Sort warps within each cluster by name
              for (const cluster in groupedWarps) {
                groupedWarps[cluster].sort((a, b) => a.name.localeCompare(b.name));
              }

              setWarpsByCluster(groupedWarps);
            } else {
              setWarpsByCluster({});
            }
          }),
        ],
        { force, onCached: () => setLoading(false) }
      );
      superseded = outcome.superseded;
      if (superseded) return;
      // 'stale' keeps the cached warps on screen; the freshness badge reports the failed re-read.
      if (outcome.status.phase === 'error') throw new Error(outcome.status.error ?? 'Failed to fetch warp data');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch warp data');
    } finally {
      if (!superseded) setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const filteredWarps: WarpSearchResult[] = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    const queryNumbers = parseNumbers(lowerCaseQuery);
    const allWarps: EnhancedWarp[] = Object.values(warpsByCluster).flat();

    // Coordinate search logic
    if (queryNumbers.length > 0) {
      const tolerance = 100;
      return allWarps
        .map(warp => {
          // The warp's position string needs to be parsed into x,y,z
          const warpCoords = parseCoordinates(warp.pos);
          if (!warpCoords) {
            return { ...warp, accuracy: undefined };
          }
          
          const difference = fuzzyMatchPartialCoordinates(queryNumbers, warpCoords, tolerance);
          
          if (difference === null) {
            return { ...warp, accuracy: undefined };
          }

          // Max difference is based on the number of coordinates in the query
          const maxDifference = queryNumbers.length * tolerance;
          const accuracy = (1 - difference / maxDifference) * 100;
          return { ...warp, accuracy };
        })
        .filter((warp): warp is WarpSearchResult & { accuracy: number } => warp.accuracy !== undefined)
        .sort((a, b) => b.accuracy - a.accuracy);
    }

    // Text search logic (fallback)
    return allWarps.filter(warp => {
      const ownerName = usernameCacheService.getName(warp.owner) || '';
      return (
        warp.cluster.toLowerCase().includes(lowerCaseQuery) ||
        warp.name.toLowerCase().includes(lowerCaseQuery) ||
        warp.owner.toLowerCase().includes(lowerCaseQuery) ||
        ownerName.toLowerCase().includes(lowerCaseQuery) ||
        warp.pos.toLowerCase().includes(lowerCaseQuery) ||
        warp.fqdn.toLowerCase().includes(lowerCaseQuery)
      );
    });
  }, [searchQuery, warpsByCluster]);

  const sortedClusters = useMemo(() => {
    return Object.keys(warpsByCluster).sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return a.localeCompare(b);
    });
  }, [warpsByCluster]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Economy & Game" title="Network Warps" description="A list of all user-created warps, grouped by cluster." icon={MapPin} />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="glass-card p-4 flex items-center gap-3">
              <SkeletonLoader className="w-12 h-12 rounded-md" />
              <SkeletonLoader className="h-4 w-40" />
              <SkeletonLoader className="h-5 w-16 rounded-full ml-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Economy & Game" title="Network Warps" description="A list of all user-created warps, grouped by cluster." icon={MapPin} />
        <EmptyState tone="danger" icon={AlertCircle} title="Failed to load warps" description={error} />
      </div>
    );
  }

  const activeWarpList = activeCluster ? warpsByCluster[activeCluster] : [];

  return (
    <LayoutGroup>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Economy & Game"
          title="Network Warps"
          description="A list of all user-created warps, grouped by cluster."
          icon={MapPin}
          meta={
            <span className="inline-flex items-center gap-3">
              <DataFreshness status={freshness} onRefresh={() => fetchData(true)} />
              <RedisKeyInfo redisKey={warpsDataKey} />
            </span>
          }
        >
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Fuzzy search by cluster, user, UUID, warp name, coords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="glass-input w-full pl-9"
              />
            </div>
            <span className="text-xs text-glass-400 tabular-nums md:text-right">
              {searchQuery ? `${filteredWarps.length} result(s) found` : `${sortedClusters.length} cluster${sortedClusters.length === 1 ? '' : 's'}`}
            </span>
          </div>
        </PageHeader>
        
        {searchQuery ? (
          <div className="space-y-4">
            {filteredWarps.length === 0 && (
              <EmptyState icon={Search} title="No warps match" description="Try a cluster, player, UUID, warp name or coordinates." />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWarps.map((warp, index) => (
                <WarpSearchResultCard
                  key={`${warp.fqdn}-${warp.name}-${index}`}
                  warp={warp}
                  query={searchQuery}
                  thumbnailUrl={clusterThumbnails[warp.cluster]}
                  accuracy={warp.accuracy}
                />
              ))}
            </div>
          </div>
        ) : (
          <motion.div 
            layout 
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"
          >
            {sortedClusters.map(clusterName => (
              <ClusterCard 
                key={clusterName}
                clusterName={clusterName}
                warps={warpsByCluster[clusterName]}
                thumbnailUrl={clusterThumbnails[clusterName]}
                onOpen={() => setActiveCluster(clusterName)}
              />
            ))}
          </motion.div>
        )}

        <AnimatePresence>
          {activeCluster && (
            <motion.div
              className="modal-overlay z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveCluster(null)}
            >
              <motion.div
                layoutId={`card-container-${activeCluster}`}
                className="glass-card overflow-hidden w-full max-w-5xl flex flex-col"
                onClick={e => e.stopPropagation()}
                transition={{ type: 'spring', duration: 0.6, bounce: 0.15 }}
              >
                {/* Header */}
                <motion.div
                  layoutId={`card-header-${activeCluster}`}
                  className="p-4 border-b border-glass-200/10 flex justify-between items-center gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {clusterThumbnails[activeCluster] && (
                      <img
                        src={clusterThumbnails[activeCluster]}
                        alt={activeCluster}
                        className="w-12 h-12 rounded-md object-cover"
                      />
                    )}
                    <h2 className="text-lg font-semibold tracking-tight text-white">{activeCluster === 'Unknown' ? 'Offline / Uncategorized' : activeCluster}</h2>
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="badge badge-accent">
                      {activeWarpList.length} {activeWarpList.length === 1 ? 'Warp' : 'Warps'}
                    </span>
                    <button type="button" onClick={() => setActiveCluster(null)} className="btn-icon" title="Close" aria-label="Close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  className="p-4 max-h-[85vh] md:max-h-[70vh] overflow-y-auto"
                  variants={modalContentVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeWarpList.map((warp, index) => (
                      <motion.div
                        key={`${warp.fqdn}-${warp.name}-${index}`}
                        variants={warpCardVariants}
                        className="subcard p-3 space-y-3"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <MapPin className="w-4 h-4 text-accent-400 shrink-0" />
                          <h3 className="text-sm font-semibold text-white truncate">
                            {warp.name}
                          </h3>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="text-glass-400">Owner:</span>
                            <PlayerProfile uuid={warp.owner} />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-glass-400">Server:</span>
                            <span className="font-mono text-xs text-glass-300">{warp.fqdn}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-glass-400">Position:</span>
                            <span className="font-mono text-xs text-glass-300">{warp.pos}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </LayoutGroup>
  );
}
